import React from 'react';
import { BsDownload } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Flex, Icon, IconButton, Tooltip } from '@chakra-ui/react';

import AddNewButton from '../../../../components/AddNewButton';
import DropdownMenu from '../../../../components/DropdownMenu';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import PageHeader from '../../../../components/PageHeader';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import SearchGlobalSearchInput from '../../../../components/search/SearchGlobalSearchInput';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import {
  exportListJobsToExcel,
  selectListJobsFilters,
  setListJobsFiltersProjectStatus
} from '../../../../redux/modules/jobs';
import { jobSearch } from '../../../../redux/modules/jobSearch';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import { hideTray, showTray, updateTrayProps } from '../../../../redux/modules/tray';
import CreateJobForm from '../CreateJobForm';
import JobsFilter from '../JobsFilter';

const JobsPageHeader = () => {
  const { companyId } = useParams();
  const dispatch = useDispatch();
  const jobSearchIsLoading = useSelector(jobSearch.selectIsLoading);
  const jobSearchPageInfo = useSelector(jobSearch.selectPageInfo);
  const jobSearchParamsFunction = useSelector(jobSearch.selectRequestParamsFunction);
  const { projectStatusOptions, selectedProjectStatus } = useSelector(selectListJobsFilters);

  const handleStatusFilterClick = selected => dispatch(setListJobsFiltersProjectStatus(selected.value));

  const handleDownloadListProjects = () => {
    const jobSearchParams = jobSearchParamsFunction();

    dispatch(exportListJobsToExcel(companyId, jobSearchParams));
  };

  const handleFormClose = formTrayProps => {
    if (formTrayProps.formIsDirty) {
      dispatch(showModal(
        () => (
          <SaveChangesModal changedInfo="project information" />
        ),
        {
          title: 'Exit without Saving?',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              onClick: () => {
                dispatch(hideModal());
                dispatch(hideTray());
              }
            }
          }
        }
      ));
    } else {
      dispatch(hideTray());
    }

    dispatch(updateTrayProps({}));
  };

  const showFormTray = () => {
    dispatch(showTray(() => <CreateJobForm companyId={companyId} />, {
      title: 'Create New Project',
      onClose: handleFormClose,
      props: { formIsDirty: false }
    }));
  };

  return (
    <Flex
      alignItems="center"
      color="greyScale.800"
      justify="space-between"
      mb="30px"
      minW="880px"
      w="100%"
    >
      <PageHeader subtitle={jobSearchIsLoading ? 'Loading...' : `${jobSearchPageInfo.total} Total`} title="Projects" />
      <Flex data-cy={jobSearchIsLoading ? CypressIds.listProjectsSearchLoading : CypressIds.listProjectsSearchLoaded}>
        <Box mr={4}>
          <DropdownMenu
            dataAnalyticsKey={analyticsIds.projectsListPage.filterByStatusDropdown}
            idKey="id"
            labelKey="displayName"
            options={projectStatusOptions}
            selected={selectedProjectStatus.displayName}
            variant="secondary"
            width="180px"
            onSelect={handleStatusFilterClick}
          />
        </Box>
        <SearchGlobalSearchInput
          dataAnalyticsId={analyticsIds.projectsListPage.searchProjectsInput}
          mr={4}
          searchBlueprint={jobSearch}
          variant="secondary"
        />
        <JobsFilter />
        <AddNewButton
          data-cy={CypressIds.listProjectsCreateProjectButton}
          dataAnalyticsId={analyticsIds.projectsListPage.createNewProjectButton}
          tabName="New Project"
          onClick={showFormTray}
        />
        <Tooltip label="Export Projects List">
          <IconButton
            data-analytics-id={analyticsIds.projectsListPage.downloadProjectsExcelButton}
            data-cy={CypressIds.exportProjectsExcelButton}
            icon={<Icon as={BsDownload} boxSize="24px" />}
            ml={4}
            variant="accent"
            onClick={() => handleDownloadListProjects()}
          />
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export default JobsPageHeader;
